import urljoin from 'url-join';
import { api } from '@/shared/services/baseApiClient';
import { config } from '@/config/config';
import { UninstallAppRequest } from '../../shared/models';
import { GoogleBmInstalledApp, InstallGoogleBmRequest } from '../models';
import { AgentConfigRequest } from '@/features/apps/googlebm/models/agentConfigRequest';
import { VerificationStatus } from '../models/verificationStatus';
import { LaunchStatus } from '@/features/apps/googlebm/models/launchStatus';
import { GoogleBmAgent } from '../models/googleBmAgent';

class GoogleApi {
	private version = 'v1';

	public install(request: InstallGoogleBmRequest): Promise<void> {
		const url = urljoin(config().googleBmHost, this.version, 'app', 'install');
		return api.send<InstallGoogleBmRequest, void>('POST', url, request);
	}

	public uninstall(workspaceId: string, installId: string): Promise<void> {
		const request = { workspaceId, installId } as UninstallAppRequest;
		const url = urljoin(config().googleBmHost, this.version, 'app', 'uninstall');
		return api.send<UninstallAppRequest, void>('POST', url, request);
	}

	public getInstalledApp(workspaceId: string): Promise<GoogleBmInstalledApp> {
		const url = urljoin(config().googleBmHost, this.version, `app?workspaceid=${workspaceId}`);
		return api.send<void, GoogleBmInstalledApp>('GET', url);
	}

	public createAgent(workspaceId: string, request: AgentConfigRequest) {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents');
		return api.send<AgentConfigRequest, GoogleBmAgent>('POST', url, request);
	}

	public updateAgent(workspaceId: string, customAgentId: string, request: AgentConfigRequest) {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents', customAgentId);
		return api.send<AgentConfigRequest, void>('PATCH', url, request);
	}

	public requestAgentVerification(workspaceId: string, customAgentId: string): Promise<{ verificationState: VerificationStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents', customAgentId, 'verify');
		return api.send<void, { verificationState: VerificationStatus }>('POST', url);
	}

	public requestAgentLaunch(workspaceId: string, customAgentId: string): Promise<{ launchState: LaunchStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents', customAgentId, 'launch');
		return api.send<void, { launchState: LaunchStatus }>('POST', url);
	}

	public cancelVerification(workspaceId: string, customAgentId: string): Promise<{ verificationState: VerificationStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents', customAgentId, 'cancelVerification');
		return api.send<void, { verificationState: VerificationStatus }>('POST', url);
	}

	public unlaunchAgent(workspaceId: string, customAgentId: string): Promise<{ launchState: LaunchStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'agents', customAgentId, 'unlaunch');
		return api.send<void, { launchState: LaunchStatus }>('POST', url);
	}

	public requestLocationVerification(workspaceId: string, locationId: string) {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'locations', locationId, 'verify');
		return api.send<void, { verificationState: VerificationStatus }>('POST', url);
	}

	public requestLocationLaunch(workspaceId: string, locationId: string): Promise<{ launchState: LaunchStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'locations', locationId, 'launch');
		return api.send<void, { launchState: LaunchStatus }>('POST', url);
	}

	public unlaunchLocation(workspaceId: string, locationId: string): Promise<{ launchState: LaunchStatus }> {
		const url = urljoin(config().googleBmHost, this.version, 'workspaces', workspaceId, 'locations', locationId, 'unlaunch');
		return api.send<void, { launchState: LaunchStatus }>('POST', url);
	}
}

export const googleApi = new GoogleApi();
